/**
 * ポータルのウィジェット本体（G4）。
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Download, Ship } from 'lucide-react';
import { Button, DateField, EmptyState, ErrorState, Loading, RadioGroup, StatusBadge, Tabs } from '@/components';
import { formatNumber } from '@/lib/format';
import { toWareki, todayISO, weekdayJa } from '@/lib/wareki';
import { cx } from '@/lib/cx';
import { useDailyVessels, type DailyVesselDto, type PortalArea } from './api';
import { SeaAreaMap, SeaMapLegend } from './components/SeaAreaMap';
import type { PortalWidgetContext } from './widgets';
import './portal-g4.css';

const AREAS: { value: PortalArea; label: string }[] = [
  { value: '全域', label: '全域' },
  { value: '尖閣諸島周辺', label: '尖閣諸島周辺' },
];

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

/** ポータル共通の表示日・海域（URL クエリで共有。漁協は常に全域） */
export function usePortalDay(ctx: PortalWidgetContext) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('date');
  const date = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : todayISO();
  const area: PortalArea = ctx.user.organizationType === '漁協' ? '全域' : params.get('area') === '全域' ? '全域' : '尖閣諸島周辺';
  const update = (next: { date?: string; area?: PortalArea }) => {
    const p = new URLSearchParams(params);
    if (next.date) p.set('date', next.date);
    if (next.area) p.set('area', next.area);
    setParams(p, { replace: true });
  };
  return { date, area, setDate: (d: string) => update({ date: d }), setArea: (a: PortalArea) => update({ area: a }) };
}

function DatePager({ date, onChange, label }: { date: string; onChange: (d: string) => void; label: string }) {
  const today = todayISO();
  return (
    <div className="bs-daypager" role="group" aria-label="表示日">
      <Button size="sm" variant="ghost" icon={<ChevronLeft size={15} />} onClick={() => onChange(addDays(date, -1))}>
        前日
      </Button>
      <div className="bs-daypager__current">
        <span className="bs-daypager__label">{label}</span>
        <strong className="bs-daypager__date">
          {toWareki(date)}（{weekdayJa(date)}）
        </strong>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onChange(addDays(date, 1))}>
        翌日
        <ChevronRight size={15} aria-hidden />
      </Button>
      <span className="bs-daypager__pick">
        <DateField aria-label="日付を指定" value={date} onChange={(e) => e.target.value && onChange(e.target.value)} />
      </span>
      {date !== today && (
        <Button size="sm" variant="secondary" icon={<CalendarDays size={14} />} onClick={() => onChange(today)}>
          今日
        </Button>
      )}
    </div>
  );
}

/** 漁協：今日の定時連絡（L-20） */
export function TodayFixedReportsWidget(ctx: PortalWidgetContext) {
  const { date, setDate } = usePortalDay(ctx);
  const { data, isPending, error, refetch } = useDailyVessels(ctx.year, date, '全域');
  const sent = data?.vessels.filter((v) => v.fixedReportStatus === '済').length ?? 0;
  return (
    <div className="bs-today">
      <DatePager date={date} onChange={setDate} label="出港予定日" />
      {error ? (
        <ErrorState compact error={error} onRetry={() => void refetch()} />
      ) : isPending ? (
        <Loading block />
      ) : data.vessels.length === 0 ? (
        <EmptyState compact icon={<Ship />} title="この日に出港予定の船はありません" description="事前連絡で登録された出港日〜入港日にこの日を含む実施船が表示されます。" />
      ) : (
        <>
          <p className="bs-today__summary">
            出港中 <strong className="bs-num">{data.vessels.length}</strong> 隻　定時連絡 済 <strong className="bs-num">{sent}</strong>　／　未送信{' '}
            <strong className={cx('bs-num', sent < data.vessels.length && 'is-alert')}>{data.vessels.length - sent}</strong>
          </p>
          <div className="bs-widget-table bs-today__table">
            <table className="bs-table bs-g4-dense">
              <thead>
                <tr>
                  <th style={{ width: 72 }}>状態</th>
                  <th style={{ width: 40 }}>No.</th>
                  <th>船名</th>
                  <th>船長</th>
                  <th className="is-right">トン数</th>
                  <th className="is-right">人数</th>
                  <th>電話番号</th>
                  <th>出港日</th>
                  <th>帰港日</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {data.vessels.map((v, i) => (
                  <tr key={v.noticeVesselId}>
                    <td>{v.fixedReportStatus === '済' ? <StatusBadge tone="success" dot>済</StatusBadge> : <StatusBadge tone="danger" dot>未送信</StatusBadge>}</td>
                    <td className="bs-num">{i + 1}</td>
                    <td className="is-strong">{v.vesselName}</td>
                    <td>{v.captainName}</td>
                    <td className="is-right bs-num">{v.tonnage ?? ''}</td>
                    <td className="is-right bs-num">{v.crewCount}</td>
                    <td className="bs-num">{v.phone}</td>
                    <td className="bs-num">{v.departureDate}</td>
                    <td className="bs-num">{v.arrivalDate}</td>
                    <td>
                      <Link
                        className="bs-btn bs-btn--sm bs-btn--secondary"
                        to={
                          v.fixedReportId
                            ? ctx.yearPath(`fixed-reports/${v.fixedReportId}/edit`)
                            : ctx.yearPath(`fixed-reports/new?noticeVesselId=${v.noticeVesselId}&date=${date}`)
                        }
                      >
                        入力
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** 海域図（出港中の実施船。L-35 事前連絡） */
export function DailyMapWidget(ctx: PortalWidgetContext) {
  const { date, area } = usePortalDay(ctx);
  const { data, error } = useDailyVessels(ctx.year, date, area);
  if (error) return <ErrorState compact error={error} />;
  return (
    <div className="bs-widget-seamap">
      <p className="bs-widget-seamap__caption">
        {toWareki(date)}・{area}
        {data && (
          <>
            　<span className="bs-num">{data.vessels.length}</span> 隻
          </>
        )}
      </p>
      <SeaAreaMap
        counts={data?.zoneCounts ?? []}
        tone="blue"
        zoneNumbers={false}
        aria-label="出港中の実施船の海域図"
        legend={
          <>
            <SeaMapLegend tone="blue">調査海域（出港〜帰港）</SeaMapLegend>
            <span>数字：調査隻数（1 隻が複数海区を調査するため、合計は隻数と一致しません）</span>
          </>
        }
      />
    </div>
  );
}

const CSV_COLUMNS: { header: string; value: (v: DailyVesselDto, i: number) => string | number | null }[] = [
  { header: 'No.', value: (_v, i) => i + 1 },
  { header: '事業実施者', value: (v) => v.businessOperatorName },
  { header: '船名', value: (v) => v.vesselName },
  { header: 'フリガナ', value: (v) => v.nameKana },
  { header: '所有者', value: (v) => v.ownerName },
  { header: '船長', value: (v) => v.captainName },
  { header: '漁船登録番号', value: (v) => v.registrationNo },
  { header: 'トン数', value: (v) => v.tonnage },
  { header: '事前連絡乗組員数', value: (v) => v.crewCount },
  { header: '船舶電話', value: (v) => v.phone },
  { header: '参加船名簿全乗船人員', value: (v) => v.totalBoardingCount },
  { header: '出港日', value: (v) => v.departureDate },
  { header: '帰港日', value: (v) => v.arrivalDate },
  { header: '実施海区農林漁区番号', value: (v) => v.zoneNumbers },
  { header: '漁獲調査予定の有無', value: (v) => (v.catchSurveyPlanned ? '有' : '無') },
  { header: '漁獲物販売予定の有無', value: (v) => (v.catchSalePlanned ? '有' : '無') },
  { header: '備考', value: (v) => v.note },
];

/** 尖閣諸島周辺の一覧を CSV（UTF-8 BOM 付き）で保存（kintone HomeCountryResultTableSenkaku.tsx の CSV 出力） */
export function toCsv(rows: DailyVesselDto[]): string {
  const esc = (s: unknown) => {
    const t = String(s ?? '');
    return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  return [CSV_COLUMNS.map((c) => c.header), ...rows.map((r, i) => CSV_COLUMNS.map((c) => c.value(r, i)))].map((r) => r.map(esc).join(',')).join('\r\n');
}

function downloadCsv(rows: DailyVesselDto[], date: string, title: string) {
  const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${date.replaceAll('-', '')}_${title}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function DeparturesTable({ rows }: { rows: DailyVesselDto[] }) {
  if (rows.length === 0) return <EmptyState compact icon={<Ship />} title="該当する実施船はありません" />;
  return (
    <div className="bs-widget-table bs-departures__table">
      <table className="bs-table bs-g4-dense">
        <thead>
          <tr>
            <th style={{ width: 36 }}>No.</th>
            <th>事業実施者</th>
            <th>船名</th>
            <th>船長</th>
            <th className="is-right">ﾄﾝ数</th>
            <th className="is-right">乗組員</th>
            <th>船舶電話</th>
            <th>出港日</th>
            <th>帰港日</th>
            <th>実施海区</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v, i) => (
            <tr key={v.noticeVesselId}>
              <td className="bs-num">{i + 1}</td>
              <td>{v.businessOperatorName}</td>
              <td className="is-strong">{v.vesselName}</td>
              <td>{v.captainName}</td>
              <td className="is-right bs-num">{v.tonnage ?? ''}</td>
              <td className="is-right bs-num">{v.crewCount}</td>
              <td className="bs-num">{v.phone}</td>
              <td className="bs-num">{v.departureDate}</td>
              <td className="bs-num">{v.arrivalDate}</td>
              <td className="bs-num">{v.zoneNumbers.replaceAll('.', '・')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 基金・国・県：指定日の出港船（全域／尖閣諸島周辺） */
export function DailyDeparturesWidget(ctx: PortalWidgetContext) {
  const { date, area, setDate, setArea } = usePortalDay(ctx);
  const { data, isPending, error, refetch } = useDailyVessels(ctx.year, date, area);
  const senkaku = area === '尖閣諸島周辺';
  return (
    <div className="bs-departures">
      <DatePager date={date} onChange={setDate} label="指定日" />
      <div className="bs-departures__bar">
        <div className="bs-departures__area">
          <span className="bs-departures__label" id="portal-area-label">
            海域選択
          </span>
          <RadioGroup name="portal-area" value={area} onChange={setArea} options={AREAS} aria-labelledby="portal-area-label" />
        </div>
        {data && (
          <dl className="bs-departures__stats">
            <div>
              <dt>隻数</dt>
              <dd>
                <span className="bs-num">{data.vessels.length}</span>隻
              </dd>
            </div>
            <div>
              <dt>事業実施者数</dt>
              <dd>
                <span className="bs-num">{data.operatorCounts.length}</span>漁協
              </dd>
            </div>
          </dl>
        )}
      </div>
      {error ? (
        <ErrorState compact error={error} onRetry={() => void refetch()} />
      ) : isPending ? (
        <Loading block />
      ) : senkaku ? (
        <Tabs
          items={[
            { key: 'today', label: '当日の出港船', badge: data.vessels.length, content: <DeparturesTable rows={data.vessels} /> },
            { key: 'future', label: '入港日が指定日以降（全件）', badge: data.futureVessels.length, content: <DeparturesTable rows={data.futureVessels} /> },
          ]}
        />
      ) : (
        <DeparturesTable rows={data.vessels} />
      )}
      {senkaku && data && (
        <div className="bs-departures__foot">
          <Button
            size="sm"
            icon={<Download size={14} />}
            disabled={data.futureVessels.length === 0}
            onClick={() => downloadCsv(data.futureVessels, date, '尖閣諸島事前連絡（調査・監視事業）')}
          >
            CSV出力（入港日が指定日以降）
          </Button>
        </div>
      )}
    </div>
  );
}

/** 基金・国・県：事業実施者別隻数 */
export function OperatorCountsWidget(ctx: PortalWidgetContext) {
  const { date, area } = usePortalDay(ctx);
  const { data, error } = useDailyVessels(ctx.year, date, area);
  const total = useMemo(() => data?.operatorCounts.reduce((s, o) => s + o.count, 0) ?? 0, [data]);
  if (error) return <ErrorState compact error={error} />;
  if (!data) return <Loading block />;
  if (data.operatorCounts.length === 0) return <EmptyState compact icon={<Ship />} title="出港中の実施船はありません" />;
  return (
    <div className="bs-widget-table bs-opcounts">
      <table className="bs-table bs-g4-dense">
        <thead>
          <tr>
            <th style={{ width: 36 }}>No.</th>
            <th>事業実施者名</th>
            <th className="is-right">隻数</th>
          </tr>
        </thead>
        <tbody>
          {data.operatorCounts.map((o, i) => (
            <tr key={o.businessOperatorId ?? `x${i}`}>
              <td className="bs-num">{i + 1}</td>
              <td>{o.businessOperatorName}</td>
              <td className="is-right bs-num">{formatNumber(o.count)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>合計</td>
            <td className="is-right bs-num">{formatNumber(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
